import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn } from '../common/utils';
import {
  createNumericTransformer,
  createSha256Transformer,
  createTimestampTransformer,
} from '../common/sql';
import {
  AlternativeAnswerSchema,
  CriterionPointSchema,
  DBAlternativeAnswerSchema,
  DBCriterionPointSchema,
  DBExemplarAnswerSchema,
  DBMarkerNoteSchema,
  MarkerNoteSchema,
  DBQuestionCriterionSchema,
  DBQuestionDiagramSchema,
  DBQuestionDependencySchema,
  DBQuestionOptionSchema,
  DBQuestionSchema,
  DBQuestionStimulusLinkSchema,
  DBStimulusSchema,
  ExemplarAnswerSchema,
  QuestionCriterionSchema,
  QuestionDependencySchema,
  QuestionDiagramSchema,
  QuestionOptionSchema,
  QuestionSchema,
  QuestionStimulusLinkSchema,
  StimulusSchema,
} from './schemas';

export const QuestionCodecConfig: TransformerConfig<
  typeof QuestionSchema,
  typeof DBQuestionSchema
> = {
  name: 'Question',
  clientSchema: QuestionSchema,
  dbSchema: DBQuestionSchema,
  transformers: [
    createSha256Transformer<typeof QuestionSchema, typeof DBQuestionSchema>('selfHash', {
      nullable: true,
    }),
    createSha256Transformer<typeof QuestionSchema, typeof DBQuestionSchema>('treeHash', {
      nullable: true,
    }),
    createTimestampTransformer<typeof QuestionSchema, typeof DBQuestionSchema>('publishedAt', {
      nullable: true,
    }),
    createTimestampTransformer<typeof QuestionSchema, typeof DBQuestionSchema>('createdAt'),
    createTimestampTransformer<typeof QuestionSchema, typeof DBQuestionSchema>('updatedAt', {
      nullable: true,
    }),
  ],
};

export const StimulusCodecConfig: TransformerConfig<
  typeof StimulusSchema,
  typeof DBStimulusSchema
> = {
  name: 'Stimulus',
  clientSchema: StimulusSchema,
  dbSchema: DBStimulusSchema,
  transformers: [
    createTimestampTransformer<typeof StimulusSchema, typeof DBStimulusSchema>('createdAt'),
    createTimestampTransformer<typeof StimulusSchema, typeof DBStimulusSchema>('updatedAt', {
      nullable: true,
    }),
  ],
};

export const QuestionCriterionCodecConfig: TransformerConfig<
  typeof QuestionCriterionSchema,
  typeof DBQuestionCriterionSchema
> = {
  name: 'QuestionCriterion',
  clientSchema: QuestionCriterionSchema,
  dbSchema: DBQuestionCriterionSchema,
  transformers: [
    createTimestampTransformer<typeof QuestionCriterionSchema, typeof DBQuestionCriterionSchema>(
      'createdAt'
    ),
    createTimestampTransformer<typeof QuestionCriterionSchema, typeof DBQuestionCriterionSchema>(
      'updatedAt',
      { nullable: true }
    ),
  ],
};

export const CriterionPointCodecConfig: TransformerConfig<
  typeof CriterionPointSchema,
  typeof DBCriterionPointSchema
> = {
  name: 'CriterionPoint',
  clientSchema: CriterionPointSchema,
  dbSchema: DBCriterionPointSchema,
  transformers: [
    createTimestampTransformer<typeof CriterionPointSchema, typeof DBCriterionPointSchema>(
      'createdAt'
    ),
    createTimestampTransformer<typeof CriterionPointSchema, typeof DBCriterionPointSchema>(
      'updatedAt',
      { nullable: true }
    ),
  ],
};

export const ExemplarAnswerCodecConfig: TransformerConfig<
  typeof ExemplarAnswerSchema,
  typeof DBExemplarAnswerSchema
> = {
  name: 'ExemplarAnswer',
  clientSchema: ExemplarAnswerSchema,
  dbSchema: DBExemplarAnswerSchema,
  transformers: [
    createTimestampTransformer<typeof ExemplarAnswerSchema, typeof DBExemplarAnswerSchema>(
      'createdAt'
    ),
    createTimestampTransformer<typeof ExemplarAnswerSchema, typeof DBExemplarAnswerSchema>(
      'updatedAt',
      { nullable: true }
    ),
  ],
};

export const AlternativeAnswerCodecConfig: TransformerConfig<
  typeof AlternativeAnswerSchema,
  typeof DBAlternativeAnswerSchema
> = {
  name: 'AlternativeAnswer',
  clientSchema: AlternativeAnswerSchema,
  dbSchema: DBAlternativeAnswerSchema,
  transformers: [
    createTimestampTransformer<typeof AlternativeAnswerSchema, typeof DBAlternativeAnswerSchema>(
      'createdAt'
    ),
    createTimestampTransformer<typeof AlternativeAnswerSchema, typeof DBAlternativeAnswerSchema>(
      'updatedAt',
      { nullable: true }
    ),
  ],
};

export const MarkerNoteCodecConfig: TransformerConfig<
  typeof MarkerNoteSchema,
  typeof DBMarkerNoteSchema
> = {
  name: 'MarkerNote',
  clientSchema: MarkerNoteSchema,
  dbSchema: DBMarkerNoteSchema,
  transformers: [
    createTimestampTransformer<typeof MarkerNoteSchema, typeof DBMarkerNoteSchema>('createdAt'),
    createTimestampTransformer<typeof MarkerNoteSchema, typeof DBMarkerNoteSchema>('updatedAt', {
      nullable: true,
    }),
  ],
};

export const QuestionOptionCodecConfig: TransformerConfig<
  typeof QuestionOptionSchema,
  typeof DBQuestionOptionSchema
> = {
  name: 'QuestionOption',
  clientSchema: QuestionOptionSchema,
  dbSchema: DBQuestionOptionSchema,
  transformers: [
    createTimestampTransformer<typeof QuestionOptionSchema, typeof DBQuestionOptionSchema>(
      'createdAt'
    ),
    createTimestampTransformer<typeof QuestionOptionSchema, typeof DBQuestionOptionSchema>(
      'updatedAt',
      { nullable: true }
    ),
  ],
};

/** The bounding box is four `numeric` columns, so all four convert alike. */
export const QuestionDiagramCodecConfig: TransformerConfig<
  typeof QuestionDiagramSchema,
  typeof DBQuestionDiagramSchema
> = {
  name: 'QuestionDiagram',
  clientSchema: QuestionDiagramSchema,
  dbSchema: DBQuestionDiagramSchema,
  transformers: [
    ...(['bboxX0', 'bboxY0', 'bboxX1', 'bboxY1'] as const).map(field =>
      createNumericTransformer<typeof QuestionDiagramSchema, typeof DBQuestionDiagramSchema>(
        field,
        { nullable: true }
      )
    ),
    createTimestampTransformer<typeof QuestionDiagramSchema, typeof DBQuestionDiagramSchema>(
      'createdAt'
    ),
    createTimestampTransformer<typeof QuestionDiagramSchema, typeof DBQuestionDiagramSchema>(
      'updatedAt',
      { nullable: true }
    ),
  ],
};

export const QuestionStimulusLinkCodecConfig: TransformerConfig<
  typeof QuestionStimulusLinkSchema,
  typeof DBQuestionStimulusLinkSchema
> = {
  name: 'QuestionStimulusLink',
  clientSchema: QuestionStimulusLinkSchema,
  dbSchema: DBQuestionStimulusLinkSchema,
  transformers: [
    createTimestampTransformer<
      typeof QuestionStimulusLinkSchema,
      typeof DBQuestionStimulusLinkSchema
    >('createdAt'),
  ],
};

export const QuestionDependencyCodecConfig: TransformerConfig<
  typeof QuestionDependencySchema,
  typeof DBQuestionDependencySchema
> = {
  name: 'QuestionDependency',
  clientSchema: QuestionDependencySchema,
  dbSchema: DBQuestionDependencySchema,
  transformers: [
    createTimestampTransformer<typeof QuestionDependencySchema, typeof DBQuestionDependencySchema>(
      'createdAt'
    ),
  ],
};

export const encodeQuestionToDb = createEncodeToDbFn(QuestionCodecConfig);
export const decodeQuestionFromDb = createDecodeFromDbFn(QuestionCodecConfig);
export const encodeStimulusToDb = createEncodeToDbFn(StimulusCodecConfig);
export const decodeStimulusFromDb = createDecodeFromDbFn(StimulusCodecConfig);
export const encodeQuestionCriterionToDb = createEncodeToDbFn(QuestionCriterionCodecConfig);
export const decodeQuestionCriterionFromDb = createDecodeFromDbFn(QuestionCriterionCodecConfig);
export const encodeCriterionPointToDb = createEncodeToDbFn(CriterionPointCodecConfig);
export const decodeCriterionPointFromDb = createDecodeFromDbFn(CriterionPointCodecConfig);
export const encodeExemplarAnswerToDb = createEncodeToDbFn(ExemplarAnswerCodecConfig);
export const decodeExemplarAnswerFromDb = createDecodeFromDbFn(ExemplarAnswerCodecConfig);
export const encodeAlternativeAnswerToDb = createEncodeToDbFn(AlternativeAnswerCodecConfig);
export const decodeAlternativeAnswerFromDb = createDecodeFromDbFn(AlternativeAnswerCodecConfig);
export const encodeMarkerNoteToDb = createEncodeToDbFn(MarkerNoteCodecConfig);
export const decodeMarkerNoteFromDb = createDecodeFromDbFn(MarkerNoteCodecConfig);
export const encodeQuestionOptionToDb = createEncodeToDbFn(QuestionOptionCodecConfig);
export const decodeQuestionOptionFromDb = createDecodeFromDbFn(QuestionOptionCodecConfig);
export const encodeQuestionDiagramToDb = createEncodeToDbFn(QuestionDiagramCodecConfig);
export const decodeQuestionDiagramFromDb = createDecodeFromDbFn(QuestionDiagramCodecConfig);
export const encodeQuestionStimulusLinkToDb = createEncodeToDbFn(QuestionStimulusLinkCodecConfig);
export const decodeQuestionStimulusLinkFromDb = createDecodeFromDbFn(
  QuestionStimulusLinkCodecConfig
);
export const encodeQuestionDependencyToDb = createEncodeToDbFn(QuestionDependencyCodecConfig);
export const decodeQuestionDependencyFromDb = createDecodeFromDbFn(QuestionDependencyCodecConfig);
